import { Link } from 'react-router-dom';
import { ArrowRight, Sun, Gift, Users, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/products/ProductCard';
import { eidAdhaProducts } from '../data/products';
import { SEOHead } from '../components/SEOHead';
import { Breadcrumbs } from '../components/Breadcrumbs';

export default function EidAdhaPage() {
  return (
    <main className="pt-20 md:pt-24 bg-[#FDFCF8]" data-testid="eid-adha-page">
      <SEOHead
        title="Eid ul Adha Geschenke 2026 – Geschenkideen zum Opferfest"
        description="Die schönsten Eid ul Adha Geschenke 2026. Bedeutungsvolle Geschenkideen zum Opferfest für Familie, Kinder und Freunde. Jetzt entdecken!"
        canonical="/eid-ul-adha"
      />
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-[#D4C5A9] to-[#C4B599] overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-10 right-10 w-64 h-64 bg-white/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-[#4A5D4F]/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#2C332E]/10 rounded-full mb-6">
                <Sun className="w-4 h-4 text-[#4A5D4F]" />
                <span className="text-sm font-medium text-[#2C332E]/90">
                  Eid Mubarak
                </span>
              </div>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium text-[#2C332E] mb-6 leading-tight">
                Eid ul Adha Geschenke – Teilen & Freude bereiten
              </h1>
              <p className="text-lg md:text-xl text-[#2C332E]/80 leading-relaxed mb-8 max-w-lg">
                Eid ul Adha ist das Fest des Teilens und der Familie. 
                Finde liebevoll zusammengestellte Eid Geschenke für diesen besonderen Anlass – 
                von traditionellen Süßigkeiten bis zu modernen Geschenksets für Erwachsene und Kinder.
              </p>
              <Button
                asChild
                className="bg-[#4A5D4F] text-white hover:bg-[#3A493E] rounded-full px-8 py-6 text-base font-medium"
              >
                <Link to="/geschenke" data-testid="eid-adha-cta">
                  Eid ul Adha Geschenke entdecken
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
            <div className="hidden lg:block">
              <div className="relative">
                <div className="w-80 h-80 mx-auto bg-[#4A5D4F]/10 rounded-full flex items-center justify-center">
                  <div className="w-60 h-60 bg-[#4A5D4F]/20 rounded-full flex items-center justify-center">
                    <Sun className="w-24 h-24 text-[#4A5D4F]/80" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Eid ul Adha */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
            <div className="lg:col-span-2">
              <span className="text-sm font-medium tracking-widest uppercase text-[#4A5D4F] mb-4 block">
                Über das Fest
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-medium text-[#2C332E] mb-6">
                Was ist Eid ul Adha?
              </h2>
              <div className="prose prose-lg text-[#6B7280] space-y-4">
                <p>
                  Eid ul Adha, auch bekannt als das „Opferfest", ist einer der 
                  bedeutendsten islamischen Feiertage. Es erinnert an die 
                  Bereitschaft des Propheten Ibrahim (Abraham), seinen Sohn zu 
                  opfern, als Zeichen seiner Hingabe an Gott.
                </p>
                <p>
                  Das Fest findet am Ende der Hadsch-Pilgerfahrt statt und 
                  erstreckt sich über mehrere Tage. Zentral ist die Tradition 
                  des Opferns eines Tieres, dessen Fleisch mit Familie, 
                  Freunden und Bedürftigen geteilt wird.
                </p>
                <p>
                  Eid ul Adha ist eine Zeit der Reflexion über Opferbereitschaft, 
                  Dankbarkeit und Großzügigkeit. Es ist eine Gelegenheit, die 
                  Segnungen des Lebens zu teilen und die Gemeinschaft zu stärken.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              {[
                { icon: Gift, title: 'Großzügigkeit', text: 'Teilen und Geben' },
                { icon: Users, title: 'Gemeinschaft', text: 'Zusammenhalt stärken' },
                { icon: Heart, title: 'Hingabe', text: 'Glaube und Opferbereitschaft' },
              ].map((item, index) => (
                <div
                  key={index}
                  className="p-6 rounded-2xl bg-[#F5F5F0] hover:bg-white hover:shadow-lg transition-all duration-300"
                >
                  <item.icon className="w-8 h-8 text-[#D4C5A9] mb-4" />
                  <h3 className="font-serif text-lg font-medium text-[#2C332E] mb-1">
                    {item.title}
                  </h3>
                  <p className="text-sm text-[#6B7280]">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gift Recommendations */}
      <section className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium tracking-widest uppercase text-[#4A5D4F] mb-4 block">
              Empfehlungen
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-[#2C332E] mb-4">
              Geschenke für Eid ul Adha
            </h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">
              Sorgfältig ausgewählte Geschenkideen, die die Bedeutung des 
              Opferfestes würdigen.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {eidAdhaProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
          <div className="text-center mt-12">
            <Button
              asChild
              variant="outline"
              className="border-[#4A5D4F] text-[#4A5D4F] hover:bg-[#4A5D4F] hover:text-white rounded-full px-8 py-6"
            >
              <Link to="/geschenke" data-testid="view-all-gifts-adha">
                Alle Geschenke ansehen
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-16 md:py-24 bg-[#4A5D4F]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-white/90 italic leading-relaxed mb-6">
            &ldquo;Das wahre Opfer ist nicht das Blut, sondern die Hingabe des 
            Herzens und die Bereitschaft zu geben.&rdquo;
          </p>
          <p className="text-[#D4C5A9] font-medium">
            Eid Mubarak – Möge euer Fest gesegnet sein
          </p>
        </div>
      </section>
    </main>
  );
}
